import React, { useState, useEffect } from "react";
import { Card, Table, Typography, message, Space, Alert, Spin, Empty, Badge, Tag, Input, Button, Select, DatePicker, Form, Row, Col } from "antd";
import { InfoCircleOutlined, TrophyOutlined, CalendarOutlined, UserOutlined, SearchOutlined, ReloadOutlined, FilterOutlined } from "@ant-design/icons";
import { helper } from "@src/controls/controlHelper";
import moment from "moment";
import "./VoteInfoPage.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const PAGE_ID = 304;

export default function VoteInfoPage(props: any) {
  const [votes, setVotes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalVotes, setTotalVotes] = useState<number>(0);
  const [customerNames, setCustomerNames] = useState<{ [key: number]: string }>({});
  const [beautyQueenNames, setBeautyQueenNames] = useState<{ [key: number]: { name: string; image: string } }>({});
  const [beautyQueenList, setBeautyQueenList] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [searchUniqueId, setSearchUniqueId] = useState<string>("");
  const [searchId, setSearchId] = useState<string>("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedBeautyQueen, setSelectedBeautyQueen] = useState<number | undefined>(undefined);
  
  const params = props.query || {};
  const { roundId } = params;
  
  // Thông tin round hiện tại
  const [currentRound, setCurrentRound] = useState<any>(null);
  console.log("currentRound", currentRound);

  useEffect(() => {
    getPageInfo();
  }, []);

  const getPageInfo = async () => {
    try {
      const _pageInfo = await helper.getPage(PAGE_ID);
      setPageInfo(_pageInfo);
    } catch (error) {
      console.log("Error fetching page info: ", error);
      message.error("Có lỗi xảy ra khi tải thông tin trang");
    }
  };

  useEffect(() => {
    if (roundId && pageInfo) {
      fetchVotes(roundId, currentPage, pageSize, filters);
      fetchRoundInfo(roundId);
      fetchAllBeautyQueens();
    }
  }, [roundId, pageInfo, currentPage, pageSize, filters]);

  useEffect(() => {
    if (votes.length > 0) {
      const customerIds = votes.map(vote => vote.customerId);
      const beautyQueenIds = votes.map(vote => vote.beautyQueenId);
      fetchCustomerNames(customerIds);
      fetchBeautyQueenNames(beautyQueenIds);
    }
  }, [votes]);

  const fetchAllBeautyQueens = async () => {
    if (!pageInfo) return;
    try {
      const response: any = await helper.callPageApi(pageInfo, "find_beauty_Queen", {
        queryInput: JSON.stringify({}),
        select: "id, name"
      });
      if (response.status === 200 && response.data && response.data.data) {
        setBeautyQueenList(response.data.data);
      }
    } catch (error) {
      console.log("Error fetching beauty queens: ", error);
    }
  };

  const fetchRoundInfo = async (roundId: number) => {
    if (!pageInfo) return;
    try {
      const response: any = await helper.callPageApi(pageInfo, "find_round", {
        queryInput: JSON.stringify({ id: roundId }),
      });
      if (response.status === 200 && response.data && response.data.data && response.data.data.length > 0) {
        setCurrentRound(response.data.data[0]);
      }
    } catch (error) {
      console.log("Error fetching round info: ", error);
    }
  };

  const buildQueryFilters = () => {
    const query: any = { roundId };
    
    if (searchUniqueId) {
      query.uniqueId = { contains: searchUniqueId };
    }
    
    if (searchId && !isNaN(Number(searchId))) {
      query.id = Number(searchId);
    }
    
    if (selectedBeautyQueen) {
      query.beautyQueenId = selectedBeautyQueen;
    }
    
    if (dateRange && dateRange[0] && dateRange[1]) {
      query.createdAt = {
        gte: dateRange[0].startOf('day').valueOf(),
        lte: dateRange[1].endOf('day').valueOf()
      };
    }
    
    return query;
  };

  const fetchVotes = async (roundId: number, page: number, limit: number, filters: any) => {
    if (!pageInfo) return;
    setLoading(true);
    
    try {
      const skip = (page - 1) * limit;
      const queryFilters = buildQueryFilters();
      
      const response: any = await helper.callPageApi(pageInfo, "find_vote", {
        queryInput: JSON.stringify(queryFilters),
        select: "id, uniqueId, point, createdAt, updatedAt, customerId, beautyQueenId, round, roundId",
        skip,
        limit,
      });
      
      if (response.status === 200 && response.data && response.data.data) {
        setVotes(response.data.data);
        setTotalVotes(response.data.count);
      } else {
        throw new Error("Không thể tải dữ liệu vote");
      }
    } catch (error) {
      console.log("Error fetching votes: ", error);
      message.error("Có lỗi xảy ra khi tải thông tin vote");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerNames = async (customerIds: number[]) => {
    if (!pageInfo || customerIds.length === 0) return;
    try {
      const response: any = await helper.callPageApi(pageInfo, "find_customer", {
        queryInput: JSON.stringify({ id: customerIds })
      });
      if (response.status === 200 && response.data.data.length > 0) {
        const names = response.data.data.reduce((acc: any, customer: any) => {
          acc[customer.id] = customer.name;
          return acc;
        }, {});
        setCustomerNames(names);
      }
    } catch (error) {
      console.log("Error fetching customer names: ", error);
    }
  };

  const fetchBeautyQueenNames = async (beautyQueenIds: number[]) => {
    if (!pageInfo || beautyQueenIds.length === 0) return;
    try {
      const response: any = await helper.callPageApi(pageInfo, "find_beauty_Queen", {
        queryInput: JSON.stringify({ id: beautyQueenIds })
      });
      if (response.status === 200 && response.data.data.length > 0) {
        const names = response.data.data.reduce((acc: any, beautyQueen: any) => {
          acc[beautyQueen.id] = {
            name: beautyQueen.name,
            image: beautyQueen.image,
          };
          return acc;
        }, {});
        setBeautyQueenNames(names);
      }
    } catch (error) {
      console.log("Error fetching beauty queen names: ", error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    const newFilters = buildQueryFilters();
    setFilters(newFilters);
  };

  const handleReset = () => {
    setSearchUniqueId("");
    setSearchId("");
    setDateRange(null);
    setSelectedBeautyQueen(undefined);
    setCurrentPage(1);
    setFilters({ roundId });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Unique ID",
      dataIndex: "uniqueId",
      key: "uniqueId",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Điểm",
      dataIndex: "point",
      key: "point",
      width: 100,
      render: (point: number) => (
        <span className="point-badge">{point}</span>
      ),
    },
    {
      title: "Thời gian",
      key: "time",
      width: 180,
      render: (record: any) => (
        <div>
          <div><CalendarOutlined /> <Text strong>Tạo:</Text> {moment(record.createdAt).format("DD/MM/YYYY HH:mm")}</div>
          {record.updatedAt !== record.createdAt && (
            <div><CalendarOutlined /> <Text strong>Cập nhật:</Text> {moment(record.updatedAt).format("DD/MM/YYYY HH:mm")}</div>
          )}
        </div>
      ),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerId",
      key: "customerId",
      width: 150,
      render: (customerId: number) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {customerNames[customerId] || <span className="loading-name">Đang tải...</span>}
        </span>
      ),
    },
    {
      title: "Người đẹp",
      dataIndex: "beautyQueenId",
      key: "beautyQueenId",
      width: 200,
      render: (beautyQueenId: number) => {
        const beautyQueen = beautyQueenNames[beautyQueenId];
        const name = beautyQueen ? beautyQueen.name : "Đang tải...";
        const image = beautyQueen ? beautyQueen.image : "";
        return (
          <div className="beauty-queen-cell">
            {image ? (
              <img src={image} alt={name} className="beauty-queen-avatar" />
            ) : (
              <div className="beauty-queen-avatar" style={{ backgroundColor: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrophyOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              </div>
            )}
            <span className="beauty-queen-name">{name}</span>
          </div>
        );
      },
    },
    {
      title: "Thông tin vòng thi",
      key: "roundInfo",
      render: (record: any) => (
        <div className="round-info">
          <div className="round-info-item">
            <span className="round-info-label">Vòng thi:</span> {record.round.name}
          </div>
          <div className="round-info-item">
            <span className="round-info-label">Bắt đầu:</span> {moment(record.round.startTime).format("DD/MM/YYYY HH:mm")}
          </div>
          <div className="round-info-item">
            <span className="round-info-label">Kết thúc:</span> {moment(record.round.endTime).format("DD/MM/YYYY HH:mm")}
          </div>
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const renderSearchForm = () => {
    return (
      <Card className="vote-info-card" style={{ marginBottom: 20 }}>
        <Title level={5}>
          <FilterOutlined /> Tìm kiếm
        </Title>
        
        <div className="search-section">
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={24} md={6} lg={6}>
              <div>
                <div className="search-label">ID</div>
                <Input
                  placeholder="Nhập ID"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </div>
            </Col>
            
            <Col xs={24} sm={24} md={6} lg={6}>
              <div>
                <div className="search-label">Unique ID</div>
                <Input
                  placeholder="Nhập Unique ID"
                  value={searchUniqueId}
                  onChange={(e) => setSearchUniqueId(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </div>
            </Col>
            
            <Col xs={24} sm={24} md={6} lg={6}>
              <div>
                <div className="search-label">Người đẹp</div>
                <Select
                  placeholder="Chọn người đẹp"
                  style={{ width: '100%' }}
                  allowClear
                  value={selectedBeautyQueen ?? undefined}
                  onChange={(value) => setSelectedBeautyQueen(value as number | undefined)}
                >
                  {beautyQueenList.map(queen => (
                    <Option key={queen.id} value={queen.id}>{queen.name}</Option>
                  ))}
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={24} md={6} lg={6}>
              <div>
                <div className="search-label">Thời gian tạo</div>
                <RangePicker 
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  format="DD/MM/YYYY"
                />
              </div>
            </Col>
          </Row>
        </div>
        
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Đặt lại
          </Button>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Tìm kiếm
          </Button>
        </div>
      </Card>
    );
  };

  const renderRoundSummary = () => {
    if (!currentRound) return null;
    
    const now = moment();
    const startTime = moment(currentRound.startTime);
    const endTime = moment(currentRound.endTime);
    let status = "upcoming";
    
    if (now.isAfter(endTime)) {
      status = "ended";
    } else if (now.isAfter(startTime)) {
      status = "active";
    }
    
    return (
      <Card className="vote-info-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>{currentRound.name}</Title>
            <div style={{ marginTop: 5 }}>
              <Text type="secondary">
                <CalendarOutlined style={{ marginRight: 8 }} />
                {moment(currentRound.startTime).format("DD/MM/YYYY HH:mm")} - {moment(currentRound.endTime).format("DD/MM/YYYY HH:mm")}
              </Text>
            </div>
          </div>
          <div>
            <Tag color={status === 'active' ? 'green' : status === 'ended' ? 'red' : 'blue'}>
              {status === 'active' ? 'Đang diễn ra' : status === 'ended' ? 'Đã kết thúc' : 'Sắp diễn ra'}
            </Tag>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ padding: 16, borderRadius: 8, background: '#f0f7ff', flex: 1, textAlign: 'center' }}>
              <Title level={2} style={{ color: '#1890ff', margin: 0 }}>{totalVotes}</Title>
              <Text>Tổng số vote</Text>
            </div>
            <div style={{ padding: 16, borderRadius: 8, background: '#f6ffed', flex: 1, textAlign: 'center' }}>
              <Title level={2} style={{ color: '#52c41a', margin: 0 }}>
                {votes.length > 0 ? Math.max(...votes.map(v => v.point)) : 0}
              </Title>
              <Text>Điểm cao nhất</Text>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="vote-info-container">
      {renderRoundSummary()}
      {renderSearchForm()}
      
      <Card className="vote-info-card">
        <div className="vote-info-header">
          <Title level={4} className="vote-info-title">
            <InfoCircleOutlined />
            Thông tin Vote
          </Title>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Đang tải dữ liệu vote...</div>
          </div>
        ) : votes.length > 0 ? (
          <Table
            dataSource={votes}
            columns={columns}
            rowKey="id"
            pagination={{ 
              current: currentPage, 
              pageSize, 
              total: totalVotes,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Tổng cộng ${total} vote` 
            }}
            onChange={handleTableChange}
            className="vote-info-table"
            size="middle"
            scroll={{ x: 1000 }}
          />
        ) : (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Empty
              description={
                <div className="empty-alert">
                  <InfoCircleOutlined />
                  Không có thông tin vote nào được tìm thấy cho vòng thi này.
                </div>
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
}